import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import DropdownMenu, {
  type DropdownMenuAction,
} from 'components/DropdownMenu/DropdownMenu';
import Popoverv2, { type PopoverProps } from 'components/v2/Popover/Popover';

import './ShortcutsDropdown.scss';

export type ShortcutsDropdownAction = DropdownMenuAction & {
  customStyles?: React.CSSProperties;
};

export interface ShortcutsDropdownProps {
  actions: Array<ShortcutsDropdownAction>;
  buttonClassName?: string;
  dataTestId?: string;
  fade?: boolean;
  idPrefix?: string;
  inModal?: boolean;
  isDisabled?: boolean;
  isOpen?: boolean;
  menuClassName?: string;
  menuTestId?: string;
  placement?: PopoverProps['placement'];
  popoverClassName?: string;
  popoverTriggerMethod?: PopoverProps['popoverTriggerMethod'];
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  trigger?: JSX.Element;
  triggerAriaLabel?: string;
  wrapperClassName?: string;
}

const createPopoverId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `shortcuts_${Math.random().toString(36).slice(2)}`;

const ShortcutsDropdown: React.FC<ShortcutsDropdownProps> = ({
  actions,
  buttonClassName,
  dataTestId = 'addPageShortcuts',
  fade = false,
  idPrefix = 'addPageShortcuts',
  inModal,
  isDisabled = false,
  isOpen: isOpenProp,
  menuClassName,
  menuTestId,
  placement = 'bottom-end',
  popoverClassName = 'dropdown-menu-popover',
  popoverTriggerMethod = 'legacy',
  setIsOpen: setIsOpenProp,
  trigger,
  triggerAriaLabel = 'Actions',
  wrapperClassName,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isOpenProp ?? internalOpen;
  const setIsOpen = setIsOpenProp ?? setInternalOpen;
  const popoverId = useMemo(() => createPopoverId(), []);
  const targetId = `${idPrefix}_${popoverId}`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isInModal, setIsInModal] = useState(false);

  useEffect(() => {
    if (inModal !== undefined) return;
    setIsInModal(Boolean(wrapperRef.current?.closest('.modal')));
  }, [inModal]);

  const hasVisibleActions = actions.some(
    action => !action.hidden && action.onClick
  );

  if (!hasVisibleActions) {
    return null;
  }

  const enhancedActions = actions.map(action => ({
    ...action,
    onClick: action.onClick
      ? () => {
          action.onClick?.();
          setIsOpen(false);
        }
      : action.onClick,
  }));

  return (
    <div
      ref={wrapperRef}
      className={cx('shortcuts-dropdown', wrapperClassName)}
    >
      <Popoverv2
        buttonClassName={cx('shortcuts-dropdown-trigger', buttonClassName)}
        className={popoverClassName}
        data-testid={dataTestId}
        fade={fade}
        id={targetId}
        inModal={inModal ?? isInModal}
        isDisabled={isDisabled}
        isOpen={isOpen}
        placement={placement}
        popoverTriggerMethod={popoverTriggerMethod}
        stopPropagation
        setIsOpen={setIsOpen}
        triggerAriaLabel={triggerAriaLabel}
        trigger={
          trigger || (
            <FontAwesomeIcon
              icon={faEllipsisV}
              size="sm"
              aria-label={triggerAriaLabel}
            />
          )
        }
      >
        <DropdownMenu
          className={cx('shortcuts-dropdown-menu', menuClassName)}
          data-testid={menuTestId}
          actions={enhancedActions}
        />
      </Popoverv2>
    </div>
  );
};

export default ShortcutsDropdown;
